import { useEffect } from 'react'

import { getDragContext, setDragContext } from '../stores/dragStore'

type TouchDropZoneOptions<T> = {
  dropZoneRef: React.RefObject<HTMLElement>
  getPayload: (dragContext: unknown) => T | null
  onDrop: (
    payload: T,
    position: { clientX: number; clientY: number },
    dropZone: HTMLElement
  ) => void
  onDragOver?: (
    payload: T,
    position: { clientX: number; clientY: number },
    dropZone: HTMLElement
  ) => void
  onDragLeave?: () => void
  enabled?: boolean
}

export const useTouchDropZone = <T>({
  dropZoneRef,
  getPayload,
  onDrop,
  onDragOver,
  onDragLeave,
  enabled = true
}: TouchDropZoneOptions<T>) => {
  // No device-class check: these are touch listeners, so a mouse never
  // reaches them. Gating on the device broke finger drops on hybrids.
  useEffect(() => {
    if (!enabled) return

    const dropZone = dropZoneRef.current
    if (!dropZone) return

    const handleTouchMove = (e: TouchEvent) => {
      const ctx = getDragContext()
      if (!ctx || ctx.source !== 'marketplace') {
        onDragLeave?.()
        return
      }

      const touch = e.touches[0]
      if (!touch) return

      const payload = getPayload(ctx)
      if (!payload) return

      const rect = dropZone.getBoundingClientRect()
      const isOverDropZone =
        touch.clientX >= rect.left &&
        touch.clientX <= rect.right &&
        touch.clientY >= rect.top &&
        touch.clientY <= rect.bottom

      if (isOverDropZone) {
        onDragOver?.(
          payload,
          {
            clientX: touch.clientX,
            clientY: touch.clientY
          },
          dropZone
        )
      } else {
        onDragLeave?.()
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      const ctx = getDragContext()
      if (!ctx || ctx.source !== 'marketplace') {
        onDragLeave?.()
        return
      }

      const payload = getPayload(ctx)
      if (!payload) {
        onDragLeave?.()
        setDragContext(null)
        return
      }

      const touch = e.changedTouches[0]
      if (!touch) {
        setDragContext(null)
        onDragLeave?.()
        return
      }

      const rect = dropZone.getBoundingClientRect()
      const isOverDropZone =
        touch.clientX >= rect.left &&
        touch.clientX <= rect.right &&
        touch.clientY >= rect.top &&
        touch.clientY <= rect.bottom

      if (isOverDropZone) {
        onDrop(
          payload,
          {
            clientX: touch.clientX,
            clientY: touch.clientY
          },
          dropZone
        )
      }

      setDragContext(null)
      onDragLeave?.()
    }

    document.addEventListener('touchmove', handleTouchMove, { passive: true })
    document.addEventListener('touchend', handleTouchEnd, { passive: true })
    document.addEventListener('touchcancel', handleTouchEnd, { passive: true })
    return () => {
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      document.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [enabled, dropZoneRef, getPayload, onDrop, onDragOver, onDragLeave])
}
